import { peekFitnessGuidance } from '@/i18n/translations';
import { getExplicitFitnessMovement } from '@/lib/fitnessExerciseIdentity';
import { detectLangFromTags, normalizeLang } from '@/i18n/locale';

/** Original static instructions for exact selected advanced variants.
 * Source principles and declared product adaptations:
 * docs/research/fitness-rea-20261015/guidance-drafts/lower8/sources.json.
 * Static guidance does not admit media or infer observation/professional assessment.
 */
export const ADVANCED_EXERCISE_GUIDANCE_IDS = [
  "free-glutes-elevacao-pelvica",
  "free-glutes-bulgaro-unilateral",
  "free-legs-hack-squat",
  "free-glutes-kickback",
  "plan-kettlebell-swing",
  "plan-clean-and-press",
  "plan-push-press",
  "plan-walking-lunges"
] as const;
export type AdvancedExerciseGuidanceId = typeof ADVANCED_EXERCISE_GUIDANCE_IDS[number];
type GuidanceCopy = readonly [string, string, string, string, string];
export type AdvancedExerciseGuidanceDictionary = Record<AdvancedExerciseGuidanceId, GuidanceCopy>;
export const ADVANCED_GUIDANCE_MOVEMENT_BINDINGS = [
  [
    "free-glutes-elevacao-pelvica",
    "free-glutes-elevacao-pelvica"
  ],
  [
    "free-glutes-bulgaro-unilateral",
    "free-glutes-bulgaro-unilateral"
  ],
  [
    "free-legs-hack-squat",
    "free-legs-hack-squat"
  ],
  [
    "free-glutes-kickback",
    "free-glutes-kickback"
  ],
  [
    "plan-kettlebell-swing",
    "plan-kettlebell-swing"
  ],
  [
    "plan-clean-and-press",
    "plan-clean-and-press"
  ],
  [
    "plan-push-press",
    "plan-push-press"
  ],
  [
    "plan-walking-lunges",
    "plan-walking-lunges"
  ]
] as const;
const byMovement = new Map<string, AdvancedExerciseGuidanceId>(ADVANCED_GUIDANCE_MOVEMENT_BINDINGS);

/** Exact declared identity only; display, provider and approximate variants are not inferred. */
export function getAdvancedExerciseGuidance(exerciseName: string, lang?: string): { steps: string[]; cues: string[] } | null {
  const id = getExplicitFitnessMovement(exerciseName)?.id;
  const key = id ? byMovement.get(id) : undefined;
  if (!key) return null;
  const language = normalizeLang(lang) ?? detectLangFromTags(lang ? [lang] : []) ?? 'EN';
  const copy = peekFitnessGuidance(language, 'advanced', key);
  if (!copy) return null;
  return { steps: copy.slice(0, 3), cues: copy.slice(3) };
}
